import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { getTenantById, updateTenant } from '../api/tenantApi';
import segmentService from '../api/tenantApi';
import customPromptService from '../api/tenantApi';

const CustomPromptManager = ({ tenantId, open, onClose }) => {
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState('');
  const [promptText, setPromptText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingPrompts, setExistingPrompts] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [updatingToggle, setUpdatingToggle] = useState(false);

  useEffect(() => {
    if (tenantId) {
      fetchSegments();
      fetchExistingPrompts();
      fetchTenantSettings();
    }
  }, [tenantId, open]);

  const fetchTenantSettings = async () => {
    try {
      const response = await getTenantById(tenantId);
        console.log("🚀 ~ fetchTenantSettings ~ response:", response)
        
      setIsEnabled(response?.data?.isCustomPromptsEnabled || false);                ///cahnge here for the response 
      setError('');
      console.log("Tenant isCustomPromptsEnabled",response?.data?.isCustomPromptsEnabled );
    } catch (error) {
      console.log('Error fetching tenant settings:', error);
      setError('Failed to fetch tenant settings');
    }
  };

  const handleToggleFeature = async () => {
    try {
      setUpdatingToggle(true);
      setError('');
      
      const newValue = !isEnabled;
      await updateTenant(tenantId,{
            isCustomPromptsEnabled: newValue
          })
     
      setIsEnabled(newValue);
      setSuccess(`Custom prompts ${newValue ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error updating feature toggle:', error);
      setError('Failed to update feature toggle');
    } finally {
      setUpdatingToggle(false);
    }
  };

  const fetchSegments = async () => {
    try {
      setLoading(true);
      const response = await segmentService.getSegmentsByTenantId(tenantId);
      setSegments(response?.data);
    } catch (error) {
      console.error('Error fetching segments:', error);
      setError('Failed to fetch segments');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingPrompts = async () => {
    try {
      console.log("Tenentid in fetching Existing prompots", tenantId)
      setLoading(true);
      const response = await customPromptService.getCustomPromptByTenantId(tenantId);
      
      console.log('Fetched prompts:', response?.data);
      
      const prompts = {};
      response?.data.forEach(item => {
        prompts[item.segmentId] = item.prompt;
      });
      
      console.log('Processed prompts:', prompts);
      setExistingPrompts(prompts);
      
      // Always set the tenant-level prompt initially since that's the default view
      if (prompts['tenant']) {
        console.log('Setting initial tenant level prompt:', prompts['tenant']);
        setPromptText(prompts['tenant']);
      }
    } catch (error) {
      console.error('Error fetching existing prompts:', error);
      setError('Failed to fetch existing prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleSegmentChange = (event) => {
    const segmentId = event.target.value;
    console.log('Segment changed to:', segmentId);
    setSelectedSegment(segmentId);
    
    // For tenant level (empty segmentId), use 'tenant' key
    const key = segmentId || 'tenant';
    console.log('Looking up prompt for key:', key, 'Available prompts:', existingPrompts);
    setPromptText(existingPrompts[key] || '');
  };

  const handlePromptChange = (event) => {
    setPromptText(event.target.value);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // If selectedSegment is empty string, it's a tenant-level prompt
      const segmentId = selectedSegment || 'tenant';
      
      const input = {
        tenantId,
        segmentId,
        prompt: promptText,
      };

     // Check if prompt already exists
      if (existingPrompts[segmentId]) {
        await customPromptService.updateCustomPromptByTenantAndSegment(tenantId,segmentId,{prompt:promptText})
      } else {
        await customPromptService.createCustomPrompt(input);
      }

      // Update local state
      setExistingPrompts(prev => ({
        ...prev,
        [segmentId]: promptText
      }));

      setSuccess('Prompt saved successfully!');
    } catch (error) {
      console.error('Error saving prompt:', error);
      setError('Failed to save prompt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const segmentId = selectedSegment || 'tenant';
      
      await customPromptService.deleteCustomPromptsByTenantAndSegment(tenantId,segmentId);

      // Update local state
      const newPrompts = { ...existingPrompts };
      delete newPrompts[segmentId];
      setExistingPrompts(newPrompts);
      setPromptText('');

      setSuccess('Prompt deleted successfully!');
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setError('Failed to delete prompt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Reset selected segment and prompt text when modal opens
  useEffect(() => {
    if (open) {
      setSelectedSegment('');
      if (existingPrompts['tenant']) {
        setPromptText(existingPrompts['tenant']);
      }
    }
  }, [open, existingPrompts]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Custom Prompt Manager
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={handleToggleFeature}
                disabled={updatingToggle}
              />
            }
            label="Enable Custom Prompts"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        {isEnabled ? (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
              <InputLabel 
                id="level-select-label" 
                shrink 
                sx={{
                  background: '#fff',
                  padding: '0 8px'
                }}
              >
                Level
              </InputLabel>
              <Select
                labelId="level-select-label"
                value={selectedSegment}
                onChange={handleSegmentChange}
                label="Level"
                displayEmpty
                sx={{
                  '& .MuiSelect-select': {
                    backgroundColor: 'transparent',
                    padding: '12px',
                    minHeight: '1.4375em'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  }
                }}
                renderValue={(selected) => {
                  if (selected === "") {
                    return "Tenant Level";
                  }
                  return segments.find(s => s.id === selected)?.name || selected;
                }}
              >
                <MenuItem value="">Tenant Level</MenuItem>
                {segments.map((segment) => (
                  <MenuItem key={segment.id} value={segment.id}>
                    {segment.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1, 
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: '1rem'
                }}
              >
                Custom Prompt
              </Typography>
              <Alert 
                severity="info" 
                sx={{ mb: 2 }}
              >
                Use variables in your prompt:
                <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                  <li><strong>#company_name</strong> - Will be replaced with the company name</li>
                  <li><strong>#company_location</strong> - Will be replaced with the company location</li>
                </ul>
              </Alert>
              <textarea
                value={promptText}
                onChange={handlePromptChange}
                style={{
                  width: '100%',
                  minHeight: '200px',
                  padding: '16px',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  resize: 'vertical',
                  outline: 'none'
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!promptText || saving}
              >
                {saving ? <CircularProgress size={24} /> : 'Save Prompt'}
              </Button>
              
              {existingPrompts[selectedSegment || 'tenant'] && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Delete Prompt
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <Alert severity="info">
            Enable custom prompts to manage tenant and segment-specific prompts.
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomPromptManager; 